// Entregáveis da Fase 2 (A06 plano ABM, A14 briefing de landing page): leitura humana e (re)geração.
import { api, fmtDate } from '../api';
import { Badge, Button, Card, Empty, useAction } from './ui';

type Run = { runId: string; at: string; output: any } | null;

const List = ({ items }: { items: string[] }) => (
  <ul className="list-disc space-y-0.5 pl-5">
    {items.map((x, i) => (
      <li key={i}>{x}</li>
    ))}
  </ul>
);

const H = ({ children }: { children: string }) => <h3 className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</h3>;

export function LandingBrief({ o }: { o: any }) {
  return (
    <div className="text-sm">
      <p>
        <strong>Objetivo:</strong> {o.pageGoal}
      </p>
      <p className="mt-1">
        <strong>Endereço sugerido:</strong> <code>/{o.slug}</code> · <strong>SEO:</strong> {o.seo.title} — <span className="text-slate-600">{o.seo.metaDescription}</span>
      </p>
      <H>Seções</H>
      <ol className="list-decimal space-y-1 pl-5">
        {o.sections.map((s: any, i: number) => (
          <li key={i}>
            <strong>{s.name}</strong> <span className="text-slate-500">({s.purpose})</span>: {s.copy}
          </li>
        ))}
      </ol>
      <H>Formulário (mínimo necessário)</H>
      <ul className="space-y-0.5">
        {o.form.fields.map((f: any) => (
          <li key={f.name}>
            <code>{f.name}</code> · {f.type} · {f.required ? 'obrigatório' : 'opcional'} — <span className="text-slate-600">{f.why}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1 rounded bg-slate-50 p-2 text-xs">
        <strong>Consentimento LGPD:</strong> {o.form.consentText}
      </p>
      <H>UTMs e eventos</H>
      <p>
        utm_source={o.utm.source} · utm_medium={o.utm.medium} · utm_campaign={o.utm.campaign} · utm_content={o.utm.content}
      </p>
      <p>Eventos GA4: {o.analyticsEvents.join(', ')}</p>
      <H>Testes A/B</H>
      <ul className="space-y-1">
        {o.abTests.map((t: any, i: number) => (
          <li key={i}>
            {t.hypothesis} — A: “{t.variantA}” × B: “{t.variantB}” · métrica: {t.metric}
          </li>
        ))}
      </ul>
      <H>Checklist de publicação</H>
      <List items={o.deployChecklist} />
    </div>
  );
}

export function AbmPlan({ o }: { o: any }) {
  return (
    <div className="text-sm">
      <H>ICP</H>
      <List items={o.icp.criteria} />
      <p className="mt-1 text-slate-600">Desqualifica: {o.icp.disqualifiers.join('; ')}</p>
      <H>Camadas</H>
      <div className="grid gap-2 md:grid-cols-3">
        {o.tiers.map((t: any) => (
          <div key={t.tier} className="rounded border border-slate-200 p-2">
            <Badge>{t.tier}</Badge>
            <p className="mt-1">{t.profile}</p>
            {t.accounts.length > 0 && <p className="mt-1 text-xs">Contas: {t.accounts.join(', ')}</p>}
            <p className="mt-1 text-xs text-slate-600">Jogadas: {t.plays.join('; ')}</p>
          </div>
        ))}
      </div>
      <H>Personas</H>
      {o.personas.map((p: any, i: number) => (
        <p key={i}>
          <strong>{p.role}</strong> — dores: {p.pains.join('; ')}. Mensagens: {p.messages.join(' / ')}
        </p>
      ))}
      <H>Sinais de compra e KPIs</H>
      <p>{o.signals.join('; ')}</p>
      <p className="text-slate-600">KPIs: {o.kpis.join('; ')}</p>
      {o.hypotheses.length > 0 && (
        <>
          <H>Hipóteses</H>
          <List items={o.hypotheses} />
        </>
      )}
      <H>Validar com o comercial antes de executar</H>
      <List items={o.salesValidation} />
    </div>
  );
}

export function DeliverablesCard({ requestId, type, deliverables, canRun, onDone }: { requestId: string; type: string; deliverables: Record<string, Run>; canRun: boolean; onDone: () => void }) {
  const act = useAction();
  const items = [
    { agent: 'A14', title: 'Briefing de landing page (A14)', show: type === 'web' || !!deliverables.A14 },
    { agent: 'A06', title: 'Plano ABM (A06)', show: type === 'abm' || !!deliverables.A06 },
  ].filter((x) => x.show);
  if (!items.length) return null;
  return (
    <>
      {items.map(({ agent, title }) => {
        const d = deliverables[agent];
        return (
          <Card
            key={agent}
            title={title}
            className="lg:col-span-3"
            actions={
              canRun && (
                <Button variant="secondary" disabled={act.busy} onClick={() => void act.run(() => api(`/api/requests/${requestId}/deliverables/${agent}`, { method: 'POST' }), 'Entregável gerado.').then(onDone)}>
                  {d ? 'Refazer' : 'Gerar'}
                </Button>
              )
            }
          >
            {act.error && <p className="mb-2 text-sm text-red-700">{act.error}</p>}
            {d ? (
              <>
                <p className="mb-2 text-xs text-slate-500">
                  Gerado em {fmtDate(d.at)} · para validação humana — {agent === 'A06' ? 'o gerente comercial valida contas e mensagens' : 'nada é publicado no site automaticamente'}.
                </p>
                {agent === 'A06' ? <AbmPlan o={d.output} /> : <LandingBrief o={d.output} />}
              </>
            ) : (
              <Empty>Ainda não gerado.</Empty>
            )}
          </Card>
        );
      })}
    </>
  );
}
